/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
/* eslint-disable max-statements */
import React from "react";
import { useMutation } from "@apollo/client";
import { Grid, Typography, Checkbox, FormControlLabel, Link, Button } from "@mui/material";
import { InputWithErrorLabel } from "components/haven-design/form/inputs";
import { MaterialFormikSelectAutocomplete } from "components/haven-design/form/select";
import {
  createTeamUser,
  createTeamUserVariables,
  UserRole,
} from "types/gql-types";
import { usePostgresViewer } from "components/viewer/context";
import { DialogActions, DialogContent } from "components/haven-design/modal/dialog";
import { Modal } from "components/haven-design";
import { useSnackbar } from "components/haven-design/snackbar";
import UserForm from "pages/shared-forms/user";
import { useSendEnrollmentEmail } from "pages/login/actions";
import { CREATE_USER_MUTATION } from "../../pages/team/create";
import { cleanUserFormOutput, UserFormData } from "../../pages/team/shared-create-update";
import NextButton from "./next-button";
import type { CommonStepProps } from "../step-props";
import type { TeamUser } from "../new";

type Props = {
  preloadedTeam?: TeamUser[];
  orgId?: string;
} & Pick<CommonStepProps, "formik" | "onNext">;

type SignerValues = {
  selfSigning: boolean;
  title: string;
  signerUserId: string;
};

const AddTeamMemberModal: React.FC<{
  open: boolean;
  onClose: () => void;
  orgId: string;
  onCreated?: (newUserId?: string) => void;
}> = ({ open, onClose, orgId, onCreated }) => {
  const [createUser, createUserMutation] = useMutation<createTeamUser, createTeamUserVariables>(
    CREATE_USER_MUTATION,
    { refetchQueries: ["getTeamByOrgId"] },
  );
  const { openSnackbar } = useSnackbar();
  const sendEnrollmentEmail = useSendEnrollmentEmail();

  const initialUserData = {
    firstName: "",
    lastName: "",
    title: "",
    directPhone: "",
    email: "",
    roles: [],
    lists: [],
    organizationId: orgId,
  };

  const handleSubmit = (values: UserFormData): void => {
    const userInput = cleanUserFormOutput(values);
    const variables = { data: userInput };
    createUser({ variables })
      .then(async (resp) => {
        const createdId = resp.data?.createUser?.id;
        if (createdId) {
          await sendEnrollmentEmail(createdId, openSnackbar, { redirect: false });
          onClose();
          if (onCreated) onCreated(createdId);
          return;
        }
        openSnackbar({ message: "Failed to create user", status: "error" });
      })
      .catch(() => {
        openSnackbar({ message: "Failed to create user", status: "error" });
      });
  };

  return (
    <Modal onRequestClose={onClose} isOpen={open} title="Invite Team Member" fullScreen>
      <DialogContent>
        <UserForm
          onSubmit={handleSubmit}
          initialData={initialUserData}
          isSending={createUserMutation.loading}
          formId="invite-user"
          renderActions={() => null}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          form="invite-user"
          disabled={createUserMutation.loading}
        >
          Invite
        </Button>
      </DialogActions>
    </Modal>
  );
};

const StepSignerInfo: React.FC<Props> = ({
  formik,
  onNext,
  preloadedTeam,
  orgId,
}) => {
  const viewer = usePostgresViewer();
  const isSupervisor = Boolean(viewer?.isSupervisor);
  const effectiveOrgId = orgId || viewer?.organizationId || "";

  const signerValues = (formik.values.signer || {}) as SignerValues;

  const team = preloadedTeam || [];

  const prefillTitleForUser = React.useCallback((userId: string) => {
    const userMatch = team.find((x) => x.id === userId);
    if (userMatch && userMatch.title) {
      formik.setFieldValue("signer.title", userMatch.title);
    }
  }, [team, formik]);
  const handleSignerSelectBlur = (e: { target?: { value?: string } }): void => {
    const id = e.target?.value || "";
    if (id) prefillTitleForUser(id);
  };

  const getFirstNameFromUser = (user: TeamUser): string => {
    const namePart = (user.fullName || "").trim().split(/\s+/)[0] || "";
    if (namePart) return namePart.toLowerCase();
    return (user.email || "").split("@")[0]?.toLowerCase() || "";
  };

  const nonArchivedTeam = team.filter((user) => !user.isArchived);
  const [supervisors, nonSupervisors] = nonArchivedTeam
    .sort((a, b) => getFirstNameFromUser(a).localeCompare(getFirstNameFromUser(b)))
    .reduce<[TeamUser[], TeamUser[]]>(
    (acc, user) => {
      const index = user.roles?.includes(UserRole.SUPERVISOR) ? 0 : 1;
      acc[index].push(user);
      return acc;
    },
    [[], []],
  );

  const options = [...supervisors, ...nonSupervisors].map((user) => ({
    label: `${user.fullName || "No Name"} (${user.email})`,
    value: user.id,
  }));
  const isSupervisorUserId = React.useCallback((userId: string) => {
    const userMatch = team.find((teamMember) => teamMember.id === userId);
    return Boolean(userMatch?.roles?.includes(UserRole.SUPERVISOR));
  }, [team]);

  const selfSigning = Boolean(isSupervisor && signerValues.selfSigning);
  const selectedUser = team.find((user) => user.id === signerValues.signerUserId);
  const selectedIsSupervisor =
    selfSigning ? true : (selectedUser?.roles?.includes(UserRole.SUPERVISOR) || false);
  const hasSigner = Boolean(signerValues.signerUserId && signerValues.signerUserId.trim() !== "");
  const hasTitle = Boolean(signerValues.title && signerValues.title.trim() !== "");
  const allowNext = (selfSigning || hasSigner) && selectedIsSupervisor && hasTitle;

  const [addMemberOpen, setAddMemberOpen] = React.useState(false);
  const [attemptedNext, setAttemptedNext] = React.useState(false);

  return (
    <Grid size={{ xs: 12, md: 8, lg: 6 }} container spacing={2}>
      {isSupervisor && (
        <Grid size={{ xs: 12 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={signerValues.selfSigning}
                onChange={(e) => {
                  const { checked } = e.target as HTMLInputElement;
                  formik.setFieldValue("signer.selfSigning", checked);
                  if (checked) {
                    formik.setFieldValue("signer.signerUserId", viewer?.id || "");
                    if (viewer?.id) prefillTitleForUser(viewer.id);
                  }
                }}
              />
            }
            label="I will be signing"
          />
        </Grid>
      )}

      <Grid size={{ xs: 12 }}>
        <MaterialFormikSelectAutocomplete
          name="signer.signerUserId"
          title="Select Signer"
          options={options}
          margin="none"
          disabled={options.length === 0 || Boolean(selfSigning)}
          onBlur={handleSignerSelectBlur}
          getOptionDisabled={(value) => !isSupervisorUserId(value as string)}
        />
        {isSupervisor && (<>
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            sx={{ display: "inline-block" }}
          >
            Can&apos;t find who you&apos;re looking for?&nbsp;
            <Link
              component="button"
              type="button"
              onClick={() => setAddMemberOpen(true)}
              sx={{ mb: "3px" }}
            >
              Add Team Member
            </Link>
          </Typography>
        </>
        )}
      </Grid>

      <Grid size={{ xs: 12 }}>
        <InputWithErrorLabel
          name="signer.title"
          label="Signer Title"
          required
          margin="normal"
          size="medium"
        />
      </Grid>

      {!selectedIsSupervisor && (
        (attemptedNext || Boolean((formik.touched)?.signer?.signerUserId))
      ) && (
        <Grid size={{ xs: 12 }}>
          <Typography variant="body2" color="error">
            Supervisors must sign contracts
          </Typography>
        </Grid>
      )}

      <Grid size={{ xs: 12 }}>
        <NextButton
          onNext={() => {
            if (!allowNext) setAttemptedNext(true);
            onNext();
          }}
          isValid={allowNext}
        />
      </Grid>

      {!!effectiveOrgId && (
        <AddTeamMemberModal
          open={addMemberOpen}
          onClose={() => setAddMemberOpen(false)}
          orgId={effectiveOrgId}
          onCreated={(newUserId) => {
            if (newUserId) {
              formik.setFieldValue("signer.signerUserId", newUserId);
            }
          }}
        />
      )}
    </Grid>
  );
};

export default StepSignerInfo;
